import { Fragment, memo, useMemo, useState } from "react"
import { Combobox, ComboboxButton, ComboboxInput, Transition } from '@headlessui/react'
import { useTranslation } from "react-i18next"
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import clsx from "clsx"
import { Link } from "../../navigation";
import _ from 'lodash'

import { family_member, patent_family, trade_mark, commodity, design_family, design_registration, competitor_member } from "../../data"
import { useTrademarks } from "../../trademarks/TrademarksProvider"
import { useRoles } from "../../user/Auth"
import { familyUrl, memberUrl } from "../../patents/utils"
import { trademarkUrl } from "../../trademarks/utils"
import { commodityUrl } from "../../products/products"
import { usePatents } from "../../patents/PatentsProvider"
import { useProducts } from "../../products/ProductsProvider"
import { useDesigns } from "../../designs/DesignsProvider"
import { designFamilyUrl, designRegistrationUrl } from "../../designs/utils"
import { useExternal } from "../../external"
import { competitorMemberUrl } from "../../external/utils"
import { isEquivalent } from "./references"

export function EntityLink({entity, entityId, className = undefined}: {entity: string, entityId: number, className?: string}) {
    const {memberById, familyById} = usePatents()
    const {trademarkById} = useTrademarks()
    const {commodityById} = useProducts()
    const {familyById: designFamilyById, registrationById: designRegistrationById} = useDesigns()
    const {memberById: competitorMemberById} = useExternal()

    let url: string | undefined = undefined
    let internalReference: string | undefined = undefined

    switch(entity) {
        case (patent_family):
            const family = familyById[entityId]
            if (family) {
                url = familyUrl(family)
                internalReference = family.internalReference
            }
            break
        case (family_member):
            const member = memberById[entityId]
            if (member) {
                url = memberUrl(member)
                internalReference = member.internalReference
            }
            break
        case (trade_mark):
            const trademark = trademarkById[entityId]
            if (trademark) {
                url = trademarkUrl(trademark)
                internalReference = trademark.reference
            }
            break
        case (commodity):
            const comm = commodityById[entityId]
            if (comm) {
                url = commodityUrl(comm)
                internalReference = comm.commodityReference
            }
            break
        case (design_family):
            const df = designFamilyById[entityId]
            if (df) {
                url = designFamilyUrl(df)
                internalReference = df.reference
            }
            break
        case (design_registration):
            const dr = designRegistrationById[entityId]
            if (dr) {
                url = designRegistrationUrl(dr)
                internalReference = dr.reference
            }
            break
        case (competitor_member):
            const cm = competitorMemberById[entityId]
            if (cm) {
                url = competitorMemberUrl(cm)
                internalReference = cm.internalReference
            }
            break
    }
    if (url === undefined || internalReference === undefined) {
        return null
    } else {
        return <Link to={url} className={className}>{internalReference}</Link>
    }
}

export interface ReferenceProps {
    internalReference?: string;
    entity?: string;
    entityId?: number;
}

export function ReferenceLinker(
    { value, onChange, name, supportedEntities = [family_member, trade_mark], limitedIds = {}, disallowNoLink = false  }:
        { value: ReferenceProps, onChange: (arg: any) => void, name: string, supportedEntities: string[], limitedIds: {[key: string]: number[]}, disallowNoLink?: boolean }
) {
    const {hasBrands, hasDesigns} = useRoles()

    const { members, memberById } = usePatents()

    const {trademarks: _trademarks, trademarkById: _trademarkById} = useTrademarks()

    const {commodities, commodityById} = useProducts()

    const {families: _designFamilies, registrations: _designRegistrations, familyById: _designFamilyById, registrationById: _designRegistrationById} = useDesigns()

    const {members: _competitorMembers, memberById: _competitorMemberById} = useExternal()

    const [query, setQuery] = useState('')

    const filteredMembers = useMemo(() =>
        family_member in limitedIds
            ? limitedIds[family_member].map(id => memberById[id]).filter(Boolean)
            : members,
        [members, memberById, limitedIds])

    const filteredTrademarks = useMemo(() => {
        const trademarks = hasBrands ? _trademarks : []
        const trademarkById = hasBrands ? _trademarkById : {}

        return trade_mark in limitedIds
            ? limitedIds[trade_mark].map(id => trademarkById[id]).filter(Boolean)
            : trademarks

    }, [_trademarks, _trademarkById, hasBrands, limitedIds])

    const filteredCommodities = useMemo(() =>
        commodity in limitedIds
            ? limitedIds[commodity].map(id => commodityById[id]).filter(Boolean)
            : commodities,
        [commodities, commodityById, limitedIds])

    const filteredDesignFamilies = useMemo(() => {
        const designFamilies = hasDesigns ? _designFamilies : []
        const designFamilyById = hasDesigns ? _designFamilyById : {}

        return design_family in limitedIds
            ? limitedIds[design_family].map(id => designFamilyById[id]).filter(Boolean)
            : designFamilies
    }, [_designFamilies, _designFamilyById, hasDesigns, limitedIds])

    const filteredDesignRegistrations = useMemo(() => {
        const designRegistrations = hasDesigns ? _designRegistrations : []
        const designRegistrationById = hasDesigns ? _designRegistrationById : {}

        return design_registration in limitedIds
            ? limitedIds[design_registration].map(id => designRegistrationById[id]).filter(Boolean)
            : designRegistrations
    }, [_designRegistrations, _designRegistrationById, hasDesigns, limitedIds])

    const filteredCompetitorMembers = useMemo(() =>
        competitor_member in limitedIds
            ? limitedIds[competitor_member].map(id => _competitorMemberById[id]).filter(Boolean)
            : _competitorMembers,
        [_competitorMembers, _competitorMemberById, limitedIds])

    const references = useMemo(() => {
        const withMember = supportedEntities.indexOf(family_member) >= 0
        const withTrademark = supportedEntities.indexOf(trade_mark) >= 0
        const withCommodity = supportedEntities.indexOf(commodity) >= 0
        const withDesignFamily = supportedEntities.indexOf(design_family) >= 0
        const withDesignRegistration = supportedEntities.indexOf(design_registration) >= 0
        const withCompetitorMember = supportedEntities.indexOf(competitor_member) >= 0
        return _([
            withMember ? filteredMembers.map(({ internalReference, familyMemberId }) => ({ internalReference, entity: family_member, entityId: familyMemberId })) : [],
            // ...families.map(({internalReference, patentFamilyId}) => ({internalReference, entity: 'patent-family', entityId: patentFamilyId})), // at first only members
            withTrademark ? filteredTrademarks.map(({ reference, trademarkId }) => ({ internalReference: reference, entity: trade_mark, entityId: trademarkId })) : [],
            withCommodity ? filteredCommodities
                .filter(c => c.commodityReference) // Filter out undefined/empty references
                .map(({ commodityReference, commodityId }) => ({ internalReference: commodityReference, entity: commodity, entityId: commodityId })) : [],
            withDesignFamily ? filteredDesignFamilies
                .filter(f => f.reference)
                .map(({ reference, designFamilyId }) => ({ internalReference: reference, entity: design_family, entityId: designFamilyId })) : [],
            withDesignRegistration ? filteredDesignRegistrations
                .filter(r => r.reference)
                .map(({ reference, registrationId }) => ({ internalReference: reference, entity: design_registration, entityId: registrationId })) : [],
            withCompetitorMember ? filteredCompetitorMembers
                .filter(m => m.internalReference)
                .map(({ internalReference, competitorMemberId }) => ({ internalReference, entity: competitor_member, entityId: competitorMemberId })) : [],
        ]).flatten()
            .filter(r => r.internalReference && r.internalReference.trim() !== '') // Filter out any references with undefined/empty internalReference
            .map(r => ({...r, lcReference: r.internalReference.toLocaleLowerCase()}))
            .sortBy(({ lcReference }) => lcReference)
            .value()
    }, [filteredMembers, filteredTrademarks, filteredCommodities, filteredDesignFamilies, filteredDesignRegistrations, filteredCompetitorMembers, supportedEntities])

    //console.log({references})

    return (
        <div className="w-full">
            <Combobox value={value} onChange={v => onChange({ target: { value: v, name } })} by={isEquivalent}>
                <div className="relative mt-1">
                    <div className="relative w-full cursor-default overflow-hidden form-input bg-white text-left focus:outline-none ">
                        <ComboboxInput
                            className="w-full border-none pr-10 leading-5 focus:ring-0 focus:outline-none truncate"
                            displayValue={(reference: ReferenceProps) => reference?.internalReference ?? ''}
                            onChange={(event) => setQuery(event.target.value)}
                        />
                        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon
                                className="h-5 w-5 text-gray-400"
                                aria-hidden="true"
                            />
                        </ComboboxButton>
                    </div>
                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setQuery('')}
                    >
                        <div> {/* Leave as otherwise we get an error that the ref is missing */}
                        <ReferenceList {...{query, references, disallowNoLink, value}} />
                        </div>
                    </Transition>
                </div>
            </Combobox>
        </div>
    )
}

const ReferenceList = memo(function ReferenceList({query, references, disallowNoLink, value}: {query: string, references: (ReferenceProps & {lcReference: string})[], disallowNoLink: boolean, value?: ReferenceProps}) {
    const {t} = useTranslation()
    const lcQuery = query.toLowerCase()
    const filteredReferences = useMemo(() => lcQuery === '' ? references.slice(0, 100) : references.filter(({lcReference}) => lcReference.includes(lcQuery)), [references, lcQuery])

    // Group references by entity type
    const groupedReferences = useMemo(() => {
        const groups = _.groupBy(filteredReferences, 'entity')
        // Define order of entity types
        const entityOrder = [family_member, patent_family, trade_mark, commodity, design_family, design_registration, competitor_member]
        return entityOrder
            .filter(entity => groups[entity] && groups[entity].length > 0)
            .map(entity => ({ entity, references: groups[entity] }))
    }, [filteredReferences])

    // Entity type display names
    const getEntityLabel = (entity: string) => {
        switch(entity) {
            case family_member:
                return t('Patent', 'Patent')
            case patent_family:
                return t('Patent Family', 'Patent Family')
            case trade_mark:
                return t('Trademark', 'Trademark')
            case commodity:
                return t('Product', 'Product')
            case design_family:
                return t('Design Family', 'Design Family')
            case design_registration:
                return t('Design', 'Design')
            case competitor_member:
                return t('external', 'Patent monitoring')
            default:
                return entity
        }
    }

    return (
        <Combobox.Options className="z-10 absolute mt-1 list-none max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg border border-pcx-500 sm:text-sm">
            {!disallowNoLink &&
                <Combobox.Option
                    className={({ active }) => clsx(
                        "relative cursor-default select-none py-2 pl-10 pr-4",
                        active ? 'bg-pcx-500 text-white' : 'text-gray-900'
                    )}
                    value={{}}
                >{t('remove-link')}
                </Combobox.Option>}
            {filteredReferences.length === 0 && query !== '' ? (
                <div className="relative cursor-default select-none py-2 pl-10 pr-4 text-gray-700">
                    Nothing found.
                </div>
            ) : (
                groupedReferences.map(({ entity, references: groupRefs }) => (
                    <Fragment key={entity}>
                        <div className="px-4 py-1.5 text-xs font-semibold text-gray-600 bg-gray-50 border-t border-gray-200">
                            {getEntityLabel(entity)}:
                        </div>
                        {groupRefs.map((reference, index) => {
                            const selected = value?.internalReference === reference.internalReference
                            return (
                                <Combobox.Option
                                    key={`${entity}-${index}`}
                                    className={({ active }) => clsx(
                                        "relative cursor-default select-none py-2 px-4",
                                        active ? 'bg-pcx-500 text-white' : 'text-gray-900'
                                    )}
                                    value={reference}
                                >
                                    {({ active }) => (
                                        <>
                                            <span className={clsx("block truncate pl-6", selected ? 'font-medium' : 'font-normal')}>
                                                {reference.internalReference}
                                            </span>
                                            {selected ? (
                                                <span className={clsx(
                                                    "absolute inset-y-0 left-0 flex items-center pl-3",
                                                    active ? 'text-white' : 'text-pcx-500'
                                                )}>
                                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                </span>
                                            ) : null}
                                        </>
                                    )}
                                </Combobox.Option>
                            )
                        })}
                    </Fragment>
                ))
            )}
        </Combobox.Options>
    )
})
